import React, { ComponentProps, useRef, useState } from 'react';
import { asView } from '../../../_lib/ViewFetcher';
import IForm from '../../../types/IForm';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import { PageToPageCreateForm } from '../partials/Forms/PageToPageCreateForm';
import IUrl from '../../../types/IUrl';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { useDelayed } from '../../../hooks/useDelayed';
import { EditableAssignment, PageToPageListEditView } from '../partials/PageToPageList/PageToPageListEditView';
import { PageToPageDeleteForm } from '../partials/Forms/PageToPageDeleteForm';
import { FooterLink } from '../../../components/molecules/FooterLink/FooterLink';
import { PageToPageEditForm } from '../partials/Forms/PageToPageEditForm';

export type PageToPageEditViewProps = {
  create: IForm;
  footerLink?: IUrl;
  templatedPreview: IUrl;
  templatedGetAllAllowedRoles: IUrl;
  assignments: EditableAssignment[];
};

type ModalEdit = { type: 'edit'; form: IForm; templatedPreview: IUrl; pageId: string };
type ModalDelete = { type: 'delete'; form: IForm };
type ModalCreate = { type: 'create'; form: IForm };

function PageToPageEdit({
  assignments,
  create,
  footerLink,
  templatedPreview,
  templatedGetAllAllowedRoles,
}: PageToPageEditViewProps) {
  const { t } = useSonTranslation('PageToPage');

  const [modal, setModal] = useState<ModalCreate | ModalEdit | ModalDelete>();

  const { delayedRefresh } = useRefreshBlock();
  const addConnectionButtonRef = useRef<HTMLButtonElement>(null);

  const openCreateForm = (form: IForm) => setModal({ type: 'create', form });
  const openEditForm = (form: IForm, url: IUrl, pageId: string) =>
    setModal({
      type: 'edit',
      form,
      templatedPreview: url,
      pageId,
    });
  const openDeleteForm = (form: IForm) => setModal({ type: 'delete', form });
  const closeModal = () => setModal(undefined);

  const delayedModalClose = useDelayed(closeModal);

  const onSuccess = () => {
    delayedRefresh();
    delayedModalClose();
  };

  const switchActiveForm = () => {
    switch (modal?.type) {
      case 'create':
        return (
          <ModalDialog closeModal={closeModal} headline={t('addConnection')} initialFocusIndex={2}>
            <PageToPageCreateForm
              templatedGetAllAllowedRoles={templatedGetAllAllowedRoles}
              templatedPreview={templatedPreview}
              form={create}
              onSuccess={onSuccess}
            />
          </ModalDialog>
        );
      case 'edit':
        return (
          <ModalDialog closeModal={closeModal} headline={t('editModal.editConnection')} initialFocusIndex={1}>
            <PageToPageEditForm
              templatedGetAllAllowedRoles={templatedGetAllAllowedRoles}
              templatedPreview={modal.templatedPreview}
              form={modal.form}
              pageId={modal.pageId}
              onSuccess={onSuccess}
            />
          </ModalDialog>
        );
      case 'delete':
        return (
          <ModalDialog
            closeModal={closeModal}
            headline={t('deleteModal.deleteConnection')}
            // delete form removes item and button where we opened modal, so we need another element where to put focus
            focusOnReturn={addConnectionButtonRef}
            initialFocusIndex={1}
          >
            <PageToPageDeleteForm form={modal.form} onSuccess={onSuccess} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <BlockBar heading={t('addConnection')} hierarchy={3}>
        <BlockBarButton
          ref={addConnectionButtonRef}
          onClick={() => openCreateForm(create)}
          text={t('addConnection')}
          svgSpriteId={Icons.ADD_PLUS}
        />
      </BlockBar>
      {assignments && assignments.length > 0 && (
        <PageToPageListEditView onEditClick={openEditForm} onDeleteClick={openDeleteForm} assignments={assignments} />
      )}
      {switchActiveForm()}
      <FooterLink footerButtonLabel={t('structuralPageToPageFooterLink')} footerLink={footerLink} />
    </>
  );
}

export const PageToPageEditView = asView(PageToPageEdit);
export type PageToPageEditView = ComponentProps<typeof PageToPageEditView>;
